import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  requisitionOptionsService,
  RequisitionOption,
} from './requisitionOptions.service';

const RequisitionOptions = () => {
  const { token, isAuthenticated } = useAuth();
  const [tab, setTab] = useState(0); // 0: Exam Types, 1: Order Reasons

  const [options, setOptions] = useState<RequisitionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [editingOption, setEditingOption] = useState<RequisitionOption | null>(
    null
  );
  const [formData, setFormData] = useState({ name: '', isActive: true });

  const fetchOptions = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      setLoading(true);
      setError(null);
      let data: RequisitionOption[] = [];
      if (tab === 0) {
        data = await requisitionOptionsService.getExamTypes(token);
      } else {
        data = await requisitionOptionsService.getOrderReasons(token);
      }
      setOptions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch options');
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, tab]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (option?: RequisitionOption) => {
    if (option) {
      setEditingOption(option);
      setFormData({ name: option.name, isActive: option.isActive });
    } else {
      setEditingOption(null);
      setFormData({ name: '', isActive: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingOption(null);
    setFormData({ name: '', isActive: true });
  };

  const handleSave = async () => {
    if (!token || !formData.name.trim()) return;
    try {
      if (tab === 0) {
        if (editingOption) {
          await requisitionOptionsService.updateExamType(
            token,
            editingOption.id,
            formData.name,
            formData.isActive
          );
        } else {
          await requisitionOptionsService.createExamType(token, formData.name);
        }
      } else {
        if (editingOption) {
          await requisitionOptionsService.updateOrderReason(
            token,
            editingOption.id,
            formData.name,
            formData.isActive
          );
        } else {
          await requisitionOptionsService.createOrderReason(
            token,
            formData.name
          );
        }
      }
      showSnackbar(
        `Successfully ${editingOption ? 'updated' : 'added'} option`
      );
      handleCloseDialog();
      fetchOptions();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to save',
        'error'
      );
    }
  };

  const handleDeleteClick = (option: RequisitionOption) => {
    setEditingOption(option);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !editingOption) return;
    try {
      if (tab === 0) {
        await requisitionOptionsService.deleteExamType(token, editingOption.id);
      } else {
        await requisitionOptionsService.deleteOrderReason(
          token,
          editingOption.id
        );
      }
      showSnackbar('Option deleted successfully');
      setDeleteDialogOpen(false);
      setEditingOption(null);
      fetchOptions();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to delete',
        'error'
      );
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Requisition Options</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!isAuthenticated}
        >
          Add {tab === 0 ? 'Exam Type' : 'Order Reason'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Exam Types" />
          <Tab label="Order Reasons" />
        </Tabs>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : options.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No options found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                options.map(option => (
                  <TableRow key={option.id} hover>
                    <TableCell>{option.id}</TableCell>
                    <TableCell>{option.name}</TableCell>
                    <TableCell>
                      <Typography
                        color={option.isActive ? 'success.main' : 'error.main'}
                      >
                        {option.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(option)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(option)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingOption ? 'Edit' : 'Add'}{' '}
          {tab === 0 ? 'Exam Type' : 'Order Reason'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          {editingOption && (
            <Box display="flex" alignItems="center">
              <Typography>Active:</Typography>
              <Switch
                checked={formData.isActive}
                onChange={e =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                color="primary"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this option? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequisitionOptions;
